import { useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseStepperAccordionOptions {
  /** Section ids that start pre-completed (e.g. a section pre-validated before the drawer opens). */
  initiallyCompletedSectionIds?: string[];
}

export interface UseStepperAccordionReturn {
  /**
   * Activates `id`, unlocking it first if needed, without ever marking it completed. For a
   * programmatic jump onto a section the user has not visited yet (e.g. skipping ahead because an
   * earlier section resolved to complete before the user had to open it).
   */
  activateSection: (id: string) => void;
  /** The currently open section id, or `null` when `sectionIds` is empty. */
  activeSectionId: string | null;
  /** Whether `goBack` would move to a previous section. */
  canGoBack: boolean;
  /** Ids of sections marked completed so far. */
  completedSectionIds: string[];
  /**
   * Marks `id` completed without changing the active section — the counterpart to
   * `uncompleteSection` for sections a background effect auto-completes from form state (e.g. once
   * all of a section's required fields resolve) rather than from explicit user navigation. Ignores
   * an `id` outside `sectionIds`, mirroring `openSection`'s own defensive membership check.
   */
  completeSection: (id: string) => void;
  /** Moves to the previous section, if any, unlocking it. The section it leaves keeps its completed mark. */
  goBack: () => void;
  /** Marks the active section completed and advances to the next one, if any. */
  goNext: () => void;
  /** Whether the active section is the last one in `sectionIds`. */
  isLastSection: boolean;
  /** Reopens a section — accepts any id in `unlockedSectionIds` (a superset of `completedSectionIds`). */
  openSection: (id: string) => void;
  /** Restores the initial state: first non-pre-completed section active, initial completions kept. */
  reset: () => void;
  /** Removes `id` from `completedSectionIds` and makes it the active section (e.g. clearing a selection). */
  uncompleteSection: (id: string) => void;
  /**
   * Ids of every section that has ever been active or completed — the set `openSection` accepts.
   * Seeded from `initiallyCompletedSectionIds` plus the initial active id, and grows whenever a
   * section becomes active (`goNext`, `openSection`, `uncompleteSection`) or completed
   * (`completeSection`). Once a section is unlocked it stays clickable even if later un-completed —
   * this is what keeps a previously visited section reachable instead of relocking it.
   */
  unlockedSectionIds: string[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Headless navigation state for `StepperAccordion`. Owns which section is active and which ones
 * are completed — the drawer/page wires `goNext`/`goBack` to Next/Back buttons and
 * `openSection` to `StepperAccordion`'s `onActiveSectionChange`.
 *
 * `options.initiallyCompletedSectionIds` lets a caller start with some sections already marked
 * completed (e.g. a section pre-validated before the drawer opens) — the initial active section
 * becomes the first id not in that list, and `reset()` restores this same initial state.
 */
export function useStepperAccordion(
  sectionIds: string[],
  options?: UseStepperAccordionOptions,
): UseStepperAccordionReturn {
  const initiallyCompletedSectionIds = options?.initiallyCompletedSectionIds ?? [];
  const initialActiveId = sectionIds.find((id) => !initiallyCompletedSectionIds.includes(id)) ?? null;
  const initialUnlockedSectionIds = buildInitialUnlockedSectionIds(initiallyCompletedSectionIds, initialActiveId);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(initialActiveId);
  const [completedSectionIds, setCompletedSectionIds] = useState<string[]>(initiallyCompletedSectionIds);
  const [unlockedSectionIds, setUnlockedSectionIds] = useState<string[]>(initialUnlockedSectionIds);

  const activeIndex = activeSectionId ? sectionIds.indexOf(activeSectionId) : -1;
  const canGoBack = activeIndex > 0;
  const isLastSection = activeIndex !== -1 && activeIndex === sectionIds.length - 1;

  function unlockSection(id: string): void {
    setUnlockedSectionIds((current) => (current.includes(id) ? current : [...current, id]));
  }

  function completeSection(id: string): void {
    if (!sectionIds.includes(id)) return;
    setCompletedSectionIds((current) => (current.includes(id) ? current : [...current, id]));
    unlockSection(id);
  }

  function goNext(): void {
    if (activeSectionId === null || isLastSection) return;
    completeSection(activeSectionId);
    const nextSectionId = sectionIds[activeIndex + 1];
    setActiveSectionId(nextSectionId);
    unlockSection(nextSectionId);
  }

  function goBack(): void {
    if (!canGoBack) return;
    const previousSectionId = sectionIds[activeIndex - 1];
    setActiveSectionId(previousSectionId);
    unlockSection(previousSectionId);
  }

  function openSection(id: string): void {
    if (unlockedSectionIds.includes(id)) {
      setActiveSectionId(id);
    }
  }

  function activateSection(id: string): void {
    if (!sectionIds.includes(id)) return;
    unlockSection(id);
    setActiveSectionId(id);
  }

  function reset(): void {
    setActiveSectionId(initialActiveId);
    setCompletedSectionIds(initiallyCompletedSectionIds);
    setUnlockedSectionIds(initialUnlockedSectionIds);
  }

  function uncompleteSection(id: string): void {
    setCompletedSectionIds((current) => current.filter((sectionId) => sectionId !== id));
    setActiveSectionId(id);
    unlockSection(id);
  }

  return {
    activateSection,
    activeSectionId,
    canGoBack,
    completedSectionIds,
    completeSection,
    goBack,
    goNext,
    isLastSection,
    openSection,
    reset,
    uncompleteSection,
    unlockedSectionIds,
  };
}

// ─── Helpers (private) ──────────────────────────────────────────────────────────

/** The initial unlocked set: every pre-completed section plus the initial active section, if any. */
function buildInitialUnlockedSectionIds(
  initiallyCompletedSectionIds: string[],
  initialActiveId: string | null,
): string[] {
  if (initialActiveId === null) return [...initiallyCompletedSectionIds];
  return [...initiallyCompletedSectionIds, initialActiveId];
}
